<script setup lang="ts">
import { FormInstance, FormRules } from "element-plus";
import { computed, reactive, ref } from "vue";
import {
  FormConcurrencyMode,
  FormDefinition,
  FormType,
  WorkflowDefinition,
} from "~/api";
import { formTypeDescriptions } from "~/utils/enumDescriptions";
import { splitByCaps } from "~/utils/miscUtils";

const form = defineModel<FormDefinition>({ required: true });
const ruleFormRef = ref<FormInstance>();

const props = defineProps<{
  workflowDefinition?: WorkflowDefinition;
  complianceItem?: boolean;
  readOnly?: boolean;
}>();

const complianceItem = ref(props.complianceItem);

const validationRules = reactive<FormRules>({
  name: [{ required: true, message: "Name is required", trigger: "blur" }],
  formType: [{ required: true, trigger: "blur" }],
  formCode: [
    { required: true, message: "Form code is required", trigger: "blur" },
    {
      pattern: /^[A-Za-z0-9-_]+$/,
      message:
        "Form code can only contain letters, numbers, hyphens, and underscores",
      trigger: "blur",
      validator: (_rule, value) => {
        if (!value) {
          return true;
        }
        const isValid = !reservedCodes.includes(value);
        return isValid || "Reserved code";
      },
    },
  ],
});

const getWorkflowStatusLabel = (statusCode: string) => {
  return props.workflowDefinition?.statusList.find(
    (status) => status.code === statusCode,
  )?.label;
};

// Computed property to ensure showOnlyForWorkflowStatuses is always an array
const showOnlyForWorkflowStatuses = computed({
  get: () => form.value.metaInfo.showOnlyForWorkflowStatuses ?? [],
  set: (value) => {
    form.value.metaInfo.showOnlyForWorkflowStatuses = value;
  },
});

const reservedCodes = "Login,Workflow,Form,Org,Site,User,Entity"
  .split(",")
  .map((code) => code.trim());

const housingModuleCodes = [
  "MainForm",
  "PRS",
  "ImportedInformation",
  "PRSChecklist",
  "DraftChecklist",
  "AddDocs",
];

const workflowForms: FormType[] = [
  FormType.WORKFLOW_DEFINITION_MAIN_FORM,
  FormType.WORKFLOW_STEP,
  FormType.OTHER,
];

const complianceItemForms: FormType[] = [
  FormType.COMPLIANCE_ITEM_DEFINITION,
  FormType.OTHER,
];

const concurrencyModeOptions = [
  { value: FormConcurrencyMode.FIRST_IN_WINS, label: "First editor wins" },
  {
    value: FormConcurrencyMode.READ_ONLY_WHEN_SHARED,
    label: "Read-only when shared",
  },
  {
    value: FormConcurrencyMode.ALLOW_CONCURRENT,
    label: "Allow concurrent editing",
  },
  { value: FormConcurrencyMode.REQUEST_CONTROL, label: "Request control" },
];

const concurrencyModeDescription = computed(() => {
  switch (form.value.metaInfo.concurrencyMode) {
    case FormConcurrencyMode.READ_ONLY_WHEN_SHARED:
      return "Everyone is read-only when multiple users are viewing";
    case FormConcurrencyMode.ALLOW_CONCURRENT:
      return "All users with permission can edit simultaneously";
    case FormConcurrencyMode.REQUEST_CONTROL:
      return "First editor wins, but others can request control transfer";
    default:
      return "First user to open gets edit access";
  }
});
</script>

<template>
  <div class="formPropertiesEditor">
    <el-form
      ref="ruleFormRef"
      :model="form"
      :rules="validationRules"
      label-width="auto"
      @submit.prevent
    >
      <el-form-item label="Name" prop="name">
        <el-input
          v-model="form.name"
          style="width: 300px"
          :disabled="props.readOnly"
        />
      </el-form-item>

      <el-form-item label="Form Type" prop="formType">
        <el-select
          v-if="workflowDefinition"
          v-model="form.formType"
          style="width: 300px"
          :disabled="props.readOnly"
        >
          <template #label="{ label }">
            <span>{{ label }}</span>
          </template>
          <el-option
            v-for="(value, k) in workflowForms"
            :key="k"
            :label="formTypeDescriptions[value].label"
            :value="value"
          ></el-option>
        </el-select>
        <el-select
          v-if="complianceItem"
          v-model="form.formType"
          style="width: 300px"
          :disabled="props.readOnly"
        >
          <template #label="{ label }">
            <span>{{ label }}</span>
          </template>
          <el-option
            v-for="(value, k) in complianceItemForms"
            :key="k"
            :label="splitByCaps(value)"
            :value="value"
          ></el-option>
        </el-select>
      </el-form-item>

      <el-form-item label="Code" prop="formCode">
        <el-input
          v-model="form.formCode"
          style="width: 300px"
          :disabled="props.readOnly"
        />
        <span class="comment">
          This code is needed when it's being referenced by other forms.<br />
          Reserved codes: {{ reservedCodes.join(", ") }}<br />
          Housing module codes: {{ housingModuleCodes.join(", ") }}
        </span>
      </el-form-item>

      <el-form-item
        v-if="workflowDefinition && form.formCode"
        label="Default for Form Code"
      >
        <el-checkbox
          v-model="form.metaInfo.defaultForFormCode"
          style="width: 300px"
          :disabled="props.readOnly"
        />
        <span class="comment">
          When checked, new workflows created from this Workflow Definition will
          instantiate this version for the logical form (identified by
          FormCode).
        </span>
      </el-form-item>

      <el-form-item
        v-if="workflowDefinition && form.formCode"
        label="Disable Version Upgrades"
      >
        <el-checkbox
          v-model="form.metaInfo.disableVersionUpgrade"
          style="width: 300px"
          :disabled="props.readOnly"
        />
        <span class="comment">
          When checked, responses on this form version can no longer be
          switched/upgraded to another version.
        </span>
      </el-form-item>

      <el-form-item
        label="Multiple Instances Allowed"
        prop="multipleInstanceAllowed"
      >
        <el-checkbox
          v-model="form.metaInfo.multipleInstancesAllowed"
          style="width: 300px"
          :disabled="props.readOnly"
        />
      </el-form-item>
      <el-form-item
        v-if="form.metaInfo.multipleInstancesAllowed"
        label="Minimum Allowed"
        prop="minInstances"
      >
        <el-input-number
          v-model="form.metaInfo.minInstances"
          :min="0"
          style="width: 300px"
          :disabled="props.readOnly"
        />
      </el-form-item>
      <el-form-item
        v-if="form.metaInfo.multipleInstancesAllowed"
        label="Maximum Allowed"
        prop="maxInstances"
      >
        <el-input-number
          v-model="form.metaInfo.maxInstances"
          :min="0"
          style="width: 300px"
          :disabled="props.readOnly"
        />
      </el-form-item>
      <el-form-item
        v-if="workflowDefinition"
        label="User Created"
        prop="userCreated"
      >
        <el-checkbox
          v-model="form.metaInfo.userCreated"
          style="width: 300px"
          :disabled="props.readOnly"
        />
        <span class="comment">
          When enabled, the form is not auto-created when a workflow starts. A
          button appears in the workflow viewer to create it on demand.
        </span>
      </el-form-item>
      <el-form-item
        v-if="workflowDefinition && form.formType == FormType.WORKFLOW_STEP"
        label="Default Expanded When Status is"
        prop="displayOnStatus"
      >
        <el-select
          v-if="form.metaInfo.displayOnStatus"
          v-model="form.metaInfo.displayOnStatus"
          style="width: 300px"
          :multiple="true"
          :disabled="props.readOnly"
        >
          <el-option
            v-for="status in workflowDefinition.statusList.map(
              (status) => status.code,
            )"
            :key="status ?? ''"
            :label="getWorkflowStatusLabel(status ?? '') ?? ''"
            :value="status ?? ''"
          ></el-option>
        </el-select>
      </el-form-item>
      <el-form-item
        v-if="workflowDefinition"
        label="Show Only for Workflow Statuses"
        prop="showOnlyForWorkflowStatuses"
      >
        <el-select
          v-model="showOnlyForWorkflowStatuses"
          style="width: 300px"
          :multiple="true"
          :disabled="props.readOnly"
          placeholder="Leave empty to show for all statuses"
          clearable
        >
          <el-option
            v-for="status in workflowDefinition.statusList.map(
              (status) => status.code,
            )"
            :key="status ?? ''"
            :label="getWorkflowStatusLabel(status ?? '') ?? ''"
            :value="status ?? ''"
          ></el-option>
        </el-select>
      </el-form-item>
      <el-form-item
        v-if="workflowDefinition && form.formType == FormType.WORKFLOW_STEP"
        label="Show Status in Workflow View"
        prop="showStatusInWorkflowView"
      >
        <el-checkbox
          v-model="form.metaInfo.showStatusInWorkflowView"
          :disabled="props.readOnly"
        >
          Display form status in expansion bar title
        </el-checkbox>
      </el-form-item>
      <el-form-item label="Multi-User Edit Mode" prop="concurrencyMode">
        <el-select
          v-model="form.metaInfo.concurrencyMode"
          style="width: 300px"
          :disabled="props.readOnly"
        >
          <el-option
            v-for="mode in concurrencyModeOptions"
            :key="mode.value"
            :label="mode.label"
            :value="mode.value"
          />
        </el-select>
        <span class="comment">
          Controls concurrent editing when multiple users <b>fill in</b> this
          form. Editing the form design always uses Request Control regardless
          of this setting.<br />{{ concurrencyModeDescription }}
        </span>
      </el-form-item>
      <el-form-item
        v-if="
          [
            FormConcurrencyMode.FIRST_IN_WINS,
            FormConcurrencyMode.REQUEST_CONTROL,
          ].includes(
            form.metaInfo.concurrencyMode ?? FormConcurrencyMode.FIRST_IN_WINS,
          )
        "
        label="Idle Timeout (minutes)"
        prop="idleTimeoutMinutes"
      >
        <el-input-number
          v-model="form.metaInfo.idleTimeoutMinutes"
          :min="1"
          :max="60"
          style="width: 300px"
          :disabled="props.readOnly"
        />
        <span class="comment">
          Users are switched to read-only after this many minutes of inactivity
        </span>
      </el-form-item>
    </el-form>
  </div>
</template>
<style lang="less">
.formPropertiesEditor {
  padding: 1em;
  .el-form-item {
    margin-bottom: 1.5em;
    .el-form-item__label {
      width: 200px;
      text-align: left;
      font-weight: bold;
    }
    .el-form-item__content {
      .el-input,
      .el-select,
      .el-input-number {
        font-size: 1.2rem;
      }
    }
  }
  .comment {
    color: #555;
    margin-left: 1em;
  }
}
</style>
